import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Spin } from "antd";
import { ChevronDown, MessageSquare, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  discussEvaluationContext,
  getEvaluationContext,
  saveEvaluationContext,
} from "./service";
import styles from "./OrganizationContextPanel.module.scss";

const EMPTY = {
  industry: "",
  business_type: "",
  business_description: "",
  products_services: [],
  customer_model: "",
  evaluation_context: "",
};

const previewLine = (ctx) => {
  const bits = [
    ctx.industry,
    ctx.business_type,
    ctx.customer_model,
  ].filter(Boolean);
  return bits.join(" · ") || "";
};

const OrganizationContextPanel = ({ applyForbidden }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [discussion, setDiscussion] = useState([]);
  const [message, setMessage] = useState("");
  const [productsText, setProductsText] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await getEvaluationContext();
    if (applyForbidden?.(res)) {
      setLoading(false);
      return;
    }
    const ctx = { ...EMPTY, ...(res?.context || {}) };
    setDraft(ctx);
    setSaved(ctx);
    setProductsText((ctx.products_services || []).join(", "));
    setDiscussion(res?.discussion || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = useMemo(() => previewLine(saved), [saved]);

  const setField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    setSaving(true);
    const products = productsText
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const payload = { ...draft, products_services: products };
    const res = await saveEvaluationContext(payload);
    setSaving(false);
    if (applyForbidden?.(res)) return;
    if (res?.error || res?.error_code) return;
    const ctx = { ...EMPTY, ...(res?.context || payload) };
    setDraft(ctx);
    setSaved(ctx);
    setProductsText((ctx.products_services || []).join(", "));
    setDiscussion(res?.discussion || discussion);
  };

  const onDiscuss = async () => {
    const text = message.trim();
    if (!text) return;
    setSending(true);
    const res = await discussEvaluationContext(text);
    setSending(false);
    if (applyForbidden?.(res)) return;
    if (res?.error || res?.error_code) return;
    setDiscussion(res?.discussion || []);
    setMessage("");
  };

  return (
    <section className={styles.wrap}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.triggerLabel}>
          {t("abs.org_context_title", "Organization context")}
        </span>
        <span className={styles.triggerPreview}>
          {preview ||
            t("abs.org_context_empty", "Not set · click to add")}
        </span>
        <ChevronDown
          size={16}
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
        />
      </button>
      <p className={styles.hint}>
        {t(
          "abs.org_context_hint",
          "Click to view or edit. Context suggests relevance. It does not change membership."
        )}
      </p>
      {open ? (
        <div className={styles.panel}>
          {loading ? (
            <div className={styles.loading}>
              <Spin />
            </div>
          ) : (
            <>
              <p className={styles.principle}>
                {t(
                  "abs.org_context_principle",
                  "CONTEXT SUGGESTS. DATA PROVES. Used only to interpret relevance, never to invent audiences."
                )}
              </p>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>{t("abs.org_context_industry", "Industry")}</span>
                  <Input
                    value={draft.industry}
                    onChange={(e) => setField("industry", e.target.value)}
                  />
                </label>
                <label className={styles.field}>
                  <span>{t("abs.org_context_type", "Business type")}</span>
                  <Input
                    value={draft.business_type}
                    onChange={(e) => setField("business_type", e.target.value)}
                  />
                </label>
                <label className={styles.field}>
                  <span>{t("abs.org_context_model", "Customer model")}</span>
                  <Select
                    value={draft.customer_model || undefined}
                    allowClear
                    placeholder={t("abs.org_context_model_ph", "Select")}
                    onChange={(value) => setField("customer_model", value || "")}
                    options={[
                      { value: "B2C", label: "B2C" },
                      { value: "B2B", label: "B2B" },
                      { value: "B2B2C", label: "B2B2C" },
                    ]}
                  />
                </label>
                <label className={`${styles.field} ${styles.wide}`}>
                  <span>
                    {t(
                      "abs.org_context_products",
                      "Products / services"
                    )}
                  </span>
                  <Input
                    value={productsText}
                    onChange={(e) => setProductsText(e.target.value)}
                    placeholder={t(
                      "abs.org_context_products_ph",
                      "Comma separated"
                    )}
                  />
                </label>
                <label className={`${styles.field} ${styles.wide}`}>
                  <span>
                    {t("abs.org_context_description", "Business description")}
                  </span>
                  <Input.TextArea
                    rows={2}
                    value={draft.business_description}
                    onChange={(e) =>
                      setField("business_description", e.target.value)
                    }
                  />
                </label>
                <label className={`${styles.field} ${styles.wide}`}>
                  <span>
                    {t(
                      "abs.org_context_notes",
                      "Evaluation notes"
                    )}
                  </span>
                  <Input.TextArea
                    rows={2}
                    value={draft.evaluation_context}
                    onChange={(e) =>
                      setField("evaluation_context", e.target.value)
                    }
                  />
                </label>
              </div>
              <div className={styles.actions}>
                <Button
                  type="primary"
                  icon={<Save size={14} />}
                  loading={saving}
                  onClick={onSave}
                >
                  {t("abs.org_context_save", "Save context")}
                </Button>
              </div>
              <div className={styles.discuss}>
                <div className={styles.discussTitle}>
                  <MessageSquare size={14} />
                  {t("abs.org_context_discuss", "Discuss")}
                </div>
                <div className={styles.thread}>
                  {(discussion || []).length === 0 ? (
                    <p className={styles.threadEmpty}>
                      {t(
                        "abs.org_context_discuss_empty",
                        "Ask whether this context describes the uploaded file. It cannot change who is in a segment."
                      )}
                    </p>
                  ) : (
                    discussion.map((item, idx) => (
                      <div
                        key={`${item.role}-${idx}`}
                        className={`${styles.bubble} ${
                          item.role === "user" ? styles.bubbleUser : styles.bubbleAi
                        }`}
                      >
                        {item.text}
                      </div>
                    ))
                  )}
                </div>
                <div className={styles.compose}>
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onPressEnter={onDiscuss}
                    placeholder={t(
                      "abs.org_context_discuss_ph",
                      "Discuss this context"
                    )}
                  />
                  <Button loading={sending} onClick={onDiscuss}>
                    {t("abs.org_context_send", "Send")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default OrganizationContextPanel;
